'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { DateTime } from 'luxon';
import { ReactNode, useEffect, useState } from 'react';
import {
  FIRST_PAGE_LIMIT_CATALOG_CARDS,
  LIMIT_CATALOG_CARDS,
} from '../../../lib/consts';
import {
  getLimitOffsetForCatalogPage,
  getPathFromQuery,
  getRequestOptions,
  getRouterOptionsFromQuery,
} from '../../../utils/catalogQuery/catalogQuery';
import { useLoading } from '../../../providers/loading';
import { CatalogLayout } from './CatalogLayout';
import { extractUtmParams } from '../../../utils/utm';
import { Action, City } from '../../../models';
import { PromoCardTypes } from '../../../models/promo-card';
import { FilterQuery, ISortFilter } from '../../../models/filters';
import { Category, CategoryData } from '../../../models/action';
import { objectToSearchParams } from '../../../utils/objectToSearchParams';
import { withSuspense } from '../../../utils/withSuspense';
import { getEvents } from '../../../lib/services/backend';
import { getSeoParams } from './getSeoParams';

type ICatalogPageProps = {
  currentCity: City;
  eventsList: {
    next: string | null;
    count: number;
    results: (Action | PromoCardTypes)[];
  };
  categories: Category[];
  gorbiletRekomenduet: {
    actions: Action[];
    slug?: string;
  };
  productMicrodata: ReactNode;
  sortTypes: ISortFilter[];
  startQuery: FilterQuery;
  initialSeoCategory?: CategoryData | null;
};

function CatalogPageInner({
  currentCity,
  eventsList,
  categories,
  sortTypes,
  startQuery,
  productMicrodata,
  gorbiletRekomenduet,
  initialSeoCategory,
}: ICatalogPageProps) {
  const [eventCount, setEventCount] = useState(0);

  const router = useRouter();
  const searchParams = useSearchParams();

  const [events, setEvents] = useState(eventsList);
  const [calendarIsOpen, setCalendarIsOpen] = useState(false);
  const [openedCategory, setOpenedCategory] = useState(
    !Array.isArray(startQuery.filters.categories.category)
      ? startQuery.filters.categories.category
      : null
  );
  const [seoCategory, setSeoCategory] = useState(initialSeoCategory ?? null);
  const [isNewData, setIsNewData] = useState(false);
  const [showMobilesort, setShowMobileSort] = useState(false);
  const [mobileFiltersCount, setMobileFiltersCount] = useState(0);
  const { updateLoading } = useLoading();

  const defaultQuery = {
    dateFilterId: null,
    filters: {
      sort: [],
      filter_price__gte: [],
      filter_price__lte: [],
      date_from: [],
      date_to: [],
      categories: {
        category: [],
        subcategories: [],
      },
      pages: [],
    },
    isDataFromServer: false,
  };
  const [query, setQuery] = useState(startQuery);

  useEffect(() => {
    setQuery(startQuery);
  }, [startQuery]);

  const onChangeDateFilter = (
    value?: number,
    dateFrom?: string,
    dateTo?: string
  ) => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    let date_from: string | [] = [];
    // eslint-disable-next-line @typescript-eslint/naming-convention
    let date_to: string | [] = [];
    switch (value) {
      case 0:
      case 1:
      case 2:
      case 3:
      case 4: {
        date_from = dateFrom ?? [];
        date_to = dateTo ?? [];
        break;
      }
      case 5: {
        date_from = dateFrom
          ? DateTime.fromFormat(dateFrom, 'dd.LL.yyyy').toFormat('yyyy-LL-dd')
          : [];
        date_to = dateTo
          ? DateTime.fromFormat(dateTo, 'dd.LL.yyyy').toFormat('yyyy-LL-dd')
          : [];
        break;
      }
      default:
        break;
    }
    setQuery({
      ...query,
      dateFilterId: query.dateFilterId === value ? null : value ?? null,
      filters: {
        ...query.filters,
        date_from,
        date_to,
        pages: [1, 1],
      },
      isDataFromServer: false,
    });
  };

  const getCountOfPage = (countOfEvents: any) => {
    if (countOfEvents - FIRST_PAGE_LIMIT_CATALOG_CARDS <= 0) {
      return 1;
    }
    return Math.ceil(
      (countOfEvents - FIRST_PAGE_LIMIT_CATALOG_CARDS) / LIMIT_CATALOG_CARDS + 1
    );
  };

  const checkCategory = (category: any, subcategories: any) => {
    updateLoading(true);
    if (
      category.id === query.filters.categories.category &&
      category.children.length === query.filters.categories.subcategories.length
    ) {
      setQuery({
        ...query,
        filters: {
          ...query.filters,
          categories: {
            category: [],
            subcategories: [],
          },
          pages: [1, 1],
        },
        isDataFromServer: false,
      });
      setSeoCategory(null);
    } else {
      setQuery({
        ...query,
        filters: {
          ...query.filters,
          categories: {
            category: category.id,
            subcategories: subcategories.map((item: any) => item.id),
          },
          pages: [1, 1],
        },
        isDataFromServer: false,
      });
      setSeoCategory(category);
    }
  };

  const checkSubcategory = (idCategory: number, subcategory: CategoryData) => {
    updateLoading(true);
    if (idCategory === query.filters.categories.category) {
      const newSubcategories = [...query.filters.categories.subcategories];
      if (newSubcategories.indexOf(subcategory.id) === -1) {
        newSubcategories.push(subcategory.id);
        const currentSeoCategory = categories.find(
          (item: any) => Number(item.id) === Number(idCategory)
        );
        setSeoCategory(currentSeoCategory ?? null);
      } else {
        newSubcategories.splice(newSubcategories.indexOf(subcategory.id), 1);
        if (newSubcategories.length) {
          const category = categories.find(
            (item: any) => item.id === idCategory
          );
          const subcategoryId = newSubcategories[newSubcategories.length - 1];
          const prevSubcategory = category
            ? category.children.find((item) => item.id === subcategoryId)
            : null;
          if (newSubcategories.length > 1) {
            const currentCheckCategory = categories.find(
              (item: any) => item.id === idCategory
            );
            setSeoCategory(currentCheckCategory ?? null);
          } else {
            const currentSeoCategory = categories.find(
              (item: any) => Number(item.id) === Number(idCategory)
            );
            if (currentSeoCategory && currentSeoCategory.children.length > 1) {
              setSeoCategory(prevSubcategory ?? null);
            } else {
              setSeoCategory(currentSeoCategory ?? null);
            }
          }
        } else {
          setSeoCategory(null);
        }
      }
      setQuery({
        ...query,
        filters: {
          ...query.filters,
          categories: {
            category: newSubcategories.length > 0 ? idCategory : [],
            subcategories: newSubcategories,
          },
          pages: [1, 1],
        },
        isDataFromServer: false,
      });
    } else {
      setQuery({
        ...query,
        filters: {
          ...query.filters,
          categories: {
            category: idCategory,
            subcategories: [subcategory.id],
          },
          pages: [1, 1],
        },
        isDataFromServer: false,
      });
      const currentSeoCategory = categories.find(
        (item) => Number(item.id) === Number(idCategory)
      );
      if (currentSeoCategory && currentSeoCategory.children.length > 1) {
        setSeoCategory(subcategory);
      } else {
        setSeoCategory(currentSeoCategory ?? null);
      }
    }
  };

  const getFiltersCount = () => {
    let filtersCount: any = 0;
    if (query.filters.sort.length > 0) {
      filtersCount += 1;
    }
    if (
      typeof query.filters.filter_price__gte === 'string' &&
      typeof query.filters.filter_price__lte === 'string'
    ) {
      filtersCount += 1;
    }
    if (
      typeof query.filters.date_from === 'string' &&
      typeof query.filters.date_to === 'string'
    ) {
      filtersCount += 1;
    }
    if (
      typeof query.filters.categories.category === 'number' ||
      query.filters.categories.subcategories.length > 0
    ) {
      filtersCount += 1;
    }
    if (filtersCount === 0) {
      filtersCount = null;
    }
    return filtersCount;
  };

  const [totalCalendarPosts, setTotalCalendarPosts] = useState<null | number>(
    null
  );
  const [dataForCalendarButtonIsLoading, setDataForCalendarButtonIsLoading] =
    useState(false);
  const [calendarDates, setCalendarDates] = useState<[string, string] | null>(
    null
  );
  function onCalendarDateChanged(dates: [string, string] | null) {
    if (dates) {
      setCalendarDates(dates);
      const startDate2 = (dates || []).join('-');
      const [dateFrom, dateTo] = startDate2
        .split('-')
        .map((dateStr: any) => dateStr.split('.').reverse().join('-'));
      setDataForCalendarButtonIsLoading(true);
      getEvents({
        categories: query.filters.categories.subcategories.join(','),
        filter_price__lte: query.filters.filter_price__lte as number,
        filter_price__gte: query.filters.filter_price__gte as number,
        offset: 0,
        limit: 1,
        is_web: true,
        status: 'active',
        city: currentCity.id,
        date_from: dateFrom,
        date_to: dateTo,
      })
        .then((response) => setTotalCalendarPosts(response.count))
        .finally(() => {
          setDataForCalendarButtonIsLoading(false);
        });
    } else {
      setCalendarDates(null);
    }
  }
  function onCalendarSubmit(initDates: [string, string] | null) {
    window.scrollTo(0, 0);
    setCalendarIsOpen(!calendarIsOpen);
    if (initDates) {
      onChangeDateFilter(5, ...initDates);
    } else {
      onChangeDateFilter();
    }
  }

  useEffect(() => {
    setCalendarIsOpen(false);
    const [startDateFrom, startDateTo] = searchParams.getAll('start_date2');

    const routerDateFrom = startDateFrom
      ? DateTime.fromFormat(startDateFrom, 'dd.LL.yyyy').toFormat('yyyy-LL-dd')
      : [];
    const routerDateTo = startDateTo
      ? DateTime.fromFormat(startDateTo, 'dd.LL.yyyy').toFormat('yyyy-LL-dd')
      : [];
    const isDatesFromEqual =
      JSON.stringify(routerDateFrom) ===
      JSON.stringify(query.filters.date_from);
    const isDatesToEqual =
      JSON.stringify(routerDateTo) === JSON.stringify(query.filters.date_to);
    if (!isDatesFromEqual || !isDatesToEqual) {
      setQuery((oldState: any) => ({
        ...oldState,
        filters: {
          ...oldState.filters,
          date_from: routerDateFrom,
          date_to: routerDateTo,
        },
        isDataFromServer: false,
      }));
    }
  }, [router]);

  useEffect(() => {
    setMobileFiltersCount(getFiltersCount());
    const utmParams = extractUtmParams(searchParams);
    if (!query.isDataFromServer) {
      const { pathname, querySubcategoriesSlugs: querySubcategories } =
        getPathFromQuery({
          defaultCatalogPathnameWithCurrentCity: `/${currentCity.url}/catalog/`,
          query,
          categories,
        });
      const routerOptions = getRouterOptionsFromQuery({
        query,
        querySubcategories,
      });
      updateLoading(true);

      const nextSearchParams = objectToSearchParams({
        ...routerOptions,
        ...utmParams.utmObject,
      });
      if (!isNewData) {
        router.push(`${pathname}?${nextSearchParams.toString()}`);
      } else {
        window.history.replaceState(
          null,
          '',
          `${pathname}?${nextSearchParams.toString()}`
        );
      }

      const { limitOfCards, offsetOfCards } = getLimitOffsetForCatalogPage({
        filters: query.filters,
        isNewData,
        firstPageLimitOfCards: FIRST_PAGE_LIMIT_CATALOG_CARDS,
        limitOfCardOtherPages: LIMIT_CATALOG_CARDS,
      });
      getEvents(
        getRequestOptions({
          query,
          limitOfCards,
          offsetOfCards,
          currentCity,
          categories,
        })
      )
        .then((res) => {
          setEventCount(res.count);
          setTotalCalendarPosts(res.count);
          if (!isNewData) {
            setEvents(res);
          } else {
            setEvents({
              ...res,
              results: events.results.concat(res.results),
            });
          }
        })
        .finally(() => updateLoading(false));
      setIsNewData(false);
    }
  }, [query]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [showMobilesort]);

  function getBreadCrumbsItems() {
    if (typeof query.filters.categories.category !== 'number') {
      return [{ title: 'Каталог' }];
    }
    const currentCategory = categories.find(
      (category: any) => category.id === query.filters.categories.category
    );
    if (!currentCategory) {
      return [{ title: 'Каталог' }];
    }
    if (currentCategory.children.length === 1) {
      const childrenCategory = currentCategory.children.find(
        (subcategory: any) =>
          subcategory.id === query.filters.categories.subcategories[0]
      );
      if (!childrenCategory) {
        return [{ title: 'Каталог' }];
      }
      return [
        { title: 'Каталог', url: 'catalog' },
        {
          title: currentCategory.seo_h1 || currentCategory.title,
          url: `catalog/${currentCategory.slug}`,
        },
        {
          title: childrenCategory.seo_h1 || childrenCategory.title,
        },
      ];
    }
    if (
      currentCategory.children.length ===
      query.filters.categories.subcategories.length
    ) {
      return [
        { title: 'Каталог', url: 'catalog' },
        {
          title: currentCategory.seo_h1 || currentCategory.title,
        },
      ];
    }
    if (query.filters.categories.subcategories.length === 1) {
      const childrenCategory = currentCategory.children.find(
        (subcategory: any) =>
          subcategory.id === query.filters.categories.subcategories[0]
      );
      if (!childrenCategory) {
        return [{ title: 'Каталог' }];
      }
      return [
        { title: 'Каталог', url: 'catalog' },
        {
          title: currentCategory.seo_h1 || currentCategory.title,
          url: `catalog/${currentCategory.slug}`,
        },
        {
          title: childrenCategory.seo_h1 || childrenCategory.title,
        },
      ];
    }
    return [
      { title: 'Каталог', url: 'catalog' },
      {
        title: currentCategory.seo_h1 || currentCategory.title,
      },
    ];
  }
  return (
    <CatalogLayout
      events={events}
      setIsNewData={setIsNewData}
      getCountOfPage={getCountOfPage}
      seoCategory={seoCategory}
      productMicrodata={productMicrodata}
      currentCity={currentCity}
      getBreadCrumbsItems={getBreadCrumbsItems}
      showMobilesort={showMobilesort}
      categories={categories}
      sortTypes={sortTypes}
      eventCount={eventCount}
      query={query}
      setQuery={setQuery}
      setShowMobileSort={setShowMobileSort}
      defaultQuery={defaultQuery}
      setSeoCategory={setSeoCategory}
      setOpenedCategory={setOpenedCategory}
      openedCategory={openedCategory}
      checkCategory={checkCategory}
      checkSubcategory={checkSubcategory}
      onChangeDateFilter={onChangeDateFilter}
      setCalendarIsOpen={setCalendarIsOpen}
      calendarIsOpen={calendarIsOpen}
      onCalendarDateChanged={onCalendarDateChanged}
      onCalendarSubmit={onCalendarSubmit}
      calendarDates={calendarDates}
      totalCalendarPosts={totalCalendarPosts}
      dataForCalendarButtonIsLoading={dataForCalendarButtonIsLoading}
      getSeoParams={() => getSeoParams(currentCity, categories, query)}
      updateLoading={updateLoading}
      gorbiletRekomenduet={gorbiletRekomenduet}
      mobileFiltersCount={mobileFiltersCount}
    />
  );
}

export const CatalogPage = withSuspense(CatalogPageInner);
